import cn from 'classnames';
import { useRef, type FC } from 'react';
import {
    trackPromoClick,
    trackPromoImpressions,
    buildPromoTrackingObject,
} from '../helpers/promoTrackingHelpers';
import { useHomePageContext } from '../HomePageContext';
import { TRADE } from 'dibs-buyer-layout/exports/userTypeEnums';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';

// components
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button';

// styles
import dibsCss from 'dibs-css';
import styles from './HpSharedFeaturedBannerModule.module.css';
import { VisibilityTracker } from 'dibs-visibility-tracker/src/VisibilityTracker';

const consumerBanner = {
    image: 'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/hp_11-25-25/bottom_banner_consumer.jpg',
    mobileImage:
        'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/hp_11-25-25/bottom_banner_consumer_m.jpg',
    topic: undefined,
    title: (
        <FormattedMessage
            id="homepage.HpSharedFeaturedBannerModule.consumer.title"
            defaultMessage="A New Curation for Fall"
        />
    ),
    description: (
        <FormattedMessage
            id="homepage.HpSharedFeaturedBannerModule.consumer.11-25-25.description"
            defaultMessage="From future heirlooms to unicorn pieces, shop new arrivals suffused with history and shaped by emerging trends."
        />
    ),

    buttonText: (
        <FormattedMessage
            id="homepage.HpSharedFeaturedBannerModule.consumer.buttonText"
            defaultMessage="Shop Now"
        />
    ),
    link: '/collections/fall-catalogue/',
};

const tradeBanner = {
    image: 'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/hp_11-25-25/bottom_banner_trade.jpg',
    mobileImage:
        'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/hp_11-25-25/bottom_banner_trade_m.jpg',
    topic: (
        <FormattedMessage
            id="homepage.HpSharedFeaturedBannerModule.trade.topic"
            defaultMessage="Level Up Your Trade 1st Status"
        />
    ),
    title: (
        <FormattedMessage
            id="homepage.HpSharedFeaturedBannerModule.trade.title"
            defaultMessage="Earn More Benefits"
        />
    ),
    description: (
        <FormattedMessage
            id="homepage.HpSharedFeaturedBannerModule.trade.11-25-25.description"
            defaultMessage="Shop on 1stDibs to earn points and lock in higher status and richer benefits for the rest of 2025 and all of 2026. "
        />
    ),

    buttonText: (
        <FormattedMessage
            id="homepage.HpSharedFeaturedBannerModule.trade.buttonText"
            defaultMessage="Learn More"
        />
    ),
    link: '/trade/rewards/',
};

export const HpSharedFeaturedBannerModule: FC<{
    isMobile: boolean;
}> = ({ isMobile }) => {
    const featuredBannerRespRef = useRef(null);
    const featuredBannerMobileRef = useRef(null);
    const { userType } = useHomePageContext();
    const isTrade = userType === TRADE;
    const banner = isTrade ? tradeBanner : consumerBanner;

    const promoTrackingObject = buildPromoTrackingObject({
        index: 0,
        id: 'bottom-banner',
        name: 'Bottom Banner',
        item: { linkData: { path: banner.link } },
    });

    let bannerImage = banner.image;
    if (isMobile) {
        bannerImage = banner.mobileImage ? banner.mobileImage : banner.image;
    }

    return isMobile ? (
        <>
            <VisibilityTracker
                elementRef={featuredBannerMobileRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackPromoImpressions([promoTrackingObject]);
                    }
                }}
            />
            <Link
                href={banner.link}
                className={styles.link}
                onClick={() => trackPromoClick(promoTrackingObject)}
            >
                <div
                    className={cn(
                        dibsCss.flex,
                        dibsCss.wFull,
                        dibsCss.flexCol,
                        dibsCss.mbLarge,
                        dibsCss.mtMedium,
                        { [styles.tradeBg]: isTrade },
                        { [styles.consumerBg]: !isTrade },
                        { [dibsCss.textWhite]: isTrade }
                    )}
                    ref={featuredBannerMobileRef}
                >
                    <div>
                        <img
                            alt=""
                            sizes="100vw"
                            src={bannerImage}
                            className={cn(styles.mobileImage, dibsCss.wFull)}
                            srcSet={getSrcsetString(bannerImage)}
                        />
                    </div>
                    <div
                        className={cn(
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.pxSmall,
                            dibsCss.myMedium,
                            dibsCss.itemsCenter
                        )}
                    >
                        {banner.topic && (
                            <span
                                className={cn(dibsCss.sassyFontHeaderSmallItalic, dibsCss.mbXsmall)}
                            >
                                {banner.topic}
                            </span>
                        )}
                        <span className={cn(dibsCss.sassyFontHeaderSizeLarge, dibsCss.mbSmall)}>
                            {banner.title}
                        </span>
                        <div>
                            <Button
                                size="medium"
                                type="transparent"
                                fullWidth={false}
                                className={cn({ [styles.tradeButton]: isTrade })}
                            >
                                {banner.buttonText}
                            </Button>
                        </div>
                    </div>
                </div>
            </Link>
        </>
    ) : (
        <>
            <VisibilityTracker
                elementRef={featuredBannerRespRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackPromoImpressions([promoTrackingObject]);
                    }
                }}
            />
            <Link
                href={banner.link}
                className={styles.link}
                onClick={() => trackPromoClick(promoTrackingObject)}
            >
                <div
                    ref={featuredBannerRespRef}
                    className={cn(
                        dibsCss.flex,
                        dibsCss.wFull,
                        dibsCss.hFull,
                        dibsCss.relative,
                        dibsCss.myLarger,
                        dibsCss.flexRowReverse,
                        { [styles.tradeBg]: isTrade },
                        { [dibsCss.textWhite]: isTrade },
                        { [styles.consumerBg]: !isTrade }
                    )}
                >
                    <img
                        alt=""
                        sizes="50vw"
                        src={bannerImage}
                        className={cn(
                            dibsCss.wFull,
                            dibsCss.hFull,
                            dibsCss.basis50,
                            dibsCss.maxH500px,
                            dibsCss.objectCover
                        )}
                        srcSet={getSrcsetString(bannerImage, [960, 1280, 1600, 1920])}
                    />
                    <div
                        className={cn(
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.basis50,
                            dibsCss.prMedium,
                            dibsCss.plLarger,
                            dibsCss.gapXsmall,
                            dibsCss.justifyCenter
                        )}
                    >
                        <span className={cn(dibsCss.sassyFontHeaderMediumItalic, dibsCss.mbXsmall)}>
                            {banner.topic}
                        </span>
                        <span
                            className={cn(
                                dibsCss.sassyFontHeaderSizeXLargeTypeNormal,
                                styles.respTitle
                            )}
                        >
                            {banner.title}
                        </span>
                        <span
                            className={cn(
                                dibsCss.sassyFontHeaderLargeNormal,
                                dibsCss.mbXsmall,
                                styles.respDescription
                            )}
                        >
                            {banner.description}
                        </span>
                        <div>
                            <Button
                                size="large"
                                type="transparent"
                                fullWidth={false}
                                className={cn({ [styles.tradeButton]: isTrade })}
                            >
                                {banner.buttonText}
                            </Button>
                        </div>
                    </div>
                </div>
            </Link>
        </>
    );
};
